// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import {
    validatePhoneNumber,
    validateEmail,
    validatePostalCode,
} from './../../../helpers/validation';
import {
    DefaultButton,
    IChoiceGroupOption,
    IComboBoxOption,
    SelectableOptionMenuItemType,
    Spinner,
    SpinnerSize,
    IDropdownOption

} from 'office-ui-fabric-react';

import history from 'src/history';
import ErrorMessage from './../../../components/ErrorMessage';
import { getRepairOffer, saveSoftwareOrderDetails } from './../../../store/orderCreation/actions';
import {
    Progress,
    saveSafetyFormData,
    updateCreateOrderProgress
} from './../../../store/orderCreation';
import {
    secondSRCreationSelector,
    selectedAccessorySelector,
    selectedParentSelector,
    zipCodeSelector
} from './../../../store/orderCreation/selectors';
import { userInfoSelector } from './../../../store/user';
import { selectedCountryInfoSelector }
    from './../../../store/countries';
import { userAgentTierSelector, userSoldToIdSelector } from './../../../store/user/selector';
import {
    deviceSelector, devicePartSelector, partDeviceEligibilitySelector, fullDeviceEligibilitySelector,
} from './../../../store/deviceInfo/selectors';

import AccessorySection from './AccessorySection';
import ProblemSelection from './ProblemSelectionProps';

import Styles from './styles.module.scss';
import { logPageAction } from 'src/lib/ApplicationInsights';
import i18next from 'i18next';
import { provinceValidation } from './../../../helpers/provinceRequiredPerCountry';
import AddressVerification from 'src/components/Address/AddressVerification';
import { statusSelector } from './../../../views/createOrder/Address/selector';
import {
    validatedAddressSelector,
    GET_ADDRESS_VALIDATION_DATA,
    getAddressValidationData,
} from './../../../store/address';
import { LoadingState, actionStatusSelector } from './../../../store/actionStatus';
import { UPLOAD_SAFETY_ATTACHMENTS, attachmentInfoSelector, uploadSafetyAttachments } from './../../../store/attachments';
import { getUserTierConfig } from './../../../lib/userTierConfig';
import enableCountriesConfig from './../../../lib/OnDemandEnableConfig';

export const DESCRIPTION_REQUIRED_PROBLEM_TYPE = 'other';

const INITIAL_STATE = {
    email: '',
    firstName: '',
    lastName: '',
    phoneNumber: '',
    language: '',
    address1: '',
    address2: '',
    city: '',
    postalCode: '',
    country: '',
    stateOrProvince: '',
    validation: {
        email: '',
        firstName: '',
        lastName: '',
        phoneNumber: '',
        address1: '',
        city: '',
        country: '',
    },
};

export default function SelectRepairItems() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const sectionContainer = React.useRef<HTMLInputElement>(null);

    const [selectedProblemType, setSelectedProblemType] = React.useState<IComboBoxOption | undefined>(undefined);
    const [partOrFull, setPartOrFull] = React.useState<IChoiceGroupOption | undefined>(undefined);
    const [softwareSupport, setSoftwareSupport] = React.useState<IChoiceGroupOption | undefined>(undefined);
    const [sdtToolRan, setSdtToolRan] = React.useState<IDropdownOption | undefined>(undefined);
    const [resolutionType, setTypeOfResolution] = React.useState<IDropdownOption | undefined>(undefined);
    const [selectedParts, setSelectedParts] = React.useState<any>([]);
    const [selectedOnDemandFruParts, setSelectedOnDemandFruParts] = React.useState<any>([]);

    const [deviceProblemDescription, setDeviceProblemDescription] = React.useState<string | undefined>('');
    const [noOfRepairAttempts, setNoOfRepairAttempts] = React.useState<IComboBoxOption | undefined>(undefined);
    const [repairDetailsDescription, setRepairDetailsDescription] = React.useState<string | undefined>('');
    const userInfo: any = useSelector(state => userInfoSelector(state));
    const userAgentTier = useSelector(state => userAgentTierSelector(state));

    const attachmentsUploadStatus = useSelector((state: any) => actionStatusSelector(state)(UPLOAD_SAFETY_ATTACHMENTS));
    const uploadedAttachmentFilesInfo = useSelector((state: any) => attachmentInfoSelector(state));
    const [shouldProgress, setShouldProgress] = React.useState(false);

    const [showModal, setShowModal] = React.useState(false);
    const [selectedAddressIndex, setSelectedAddressIndex] = React.useState('');
    const addressValidationLoadingState = useSelector(state =>
        statusSelector(state)(GET_ADDRESS_VALIDATION_DATA)
    );
    const [isSafetySymptom, SetSafetySymptom] = React.useState(false);
    const userTier = getUserTierConfig(userAgentTier);

    const [cruProofOfPurchaseSelected, setCruProofOfPurchaseSelected] = React.useState(false);
    const [isCruPartDeviceEligible, setIsCruPartDeviceEligible] = React.useState(false);

    //Progress if shouldProgress is true, this is for handling uploading of files to azure
    React.useEffect(() => {
        if (shouldProgress &&
            !(attachmentsUploadStatus.status === LoadingState.success &&
                !Array.isArray(uploadedAttachmentFilesInfo?.attachmentInfo))) {
            dispatch(
                updateCreateOrderProgress(Progress.RepairOfferSelection)
            );
        }
    },
        [shouldProgress]);

    //When attachments upload is succeeded, do dispatch the safetyIssue info and set shouldProgress true
    React.useEffect(() => {
        if (attachmentsUploadStatus.status === LoadingState.success) {
            storeSafetyIssueInfo();
            setShouldProgress(true);
        } else {
            setShouldProgress(false);
        }

    }, [attachmentsUploadStatus.status]);

    function checkSafetySymptom(value: boolean) {
        SetSafetySymptom(value);
    }
    const validatedAddress = useSelector(state =>
        validatedAddressSelector(state)
    );

    function onAddressChoiceChange(
        ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption
    ): void {
        if (option) {
            setSelectedAddressIndex(option.key);
        }
    }

    function getAddressChoices() {
        let choices: any[] = [];
        if (addressValidationLoadingState.status === LoadingState.success) {
            const { addressMatches } = validatedAddress;
            if (addressMatches) {
                choices = addressMatches.map(
                    (address: any, index: any) =>
                    ({
                        key: index,
                        text: `${address.address1} ${address.address2 ||
                            ''}, ${address.city} ${address.postalCode ||
                            ''} ${address.stateOrProvince || ''} ${address.country
                            } `,
                    } as IChoiceGroupOption)
                );
            }
        }
        choices.push({ key: -1, text: t('use_address_provided') });
        return choices;
    }

    function onDismissed() {
        setShowModal(false);
        onSubmitNext();
    }

    function onCancelAddressChoicePopupClicked() {
        setShowModal(false);
    }

    function onSaveAddressChoicePopupClicked() {
        const indexChosen: number = parseInt(selectedAddressIndex, 10);

        if (indexChosen > -1 && addressValidationLoadingState.status === LoadingState.success) {
            const { addressMatches } = validatedAddress;
            if (addressMatches) {

                const validatedAddress = addressMatches[indexChosen];
                setAddressValues({
                    ...addressValues,
                    address1: validatedAddress.address1,
                    address2: validatedAddress.address2,
                    city: validatedAddress.city,
                    postalCode: validatedAddress.postalCode,
                    stateOrProvince: validatedAddress.stateOrProvince,
                    country: validatedAddress.country,
                });
            }
        }
    }

    const accessories: any[] = useSelector(state =>
        selectedAccessorySelector(state)
    );

    const [accessoriesDescription, setAccessoriesDescription] = React.useState<{} | undefined>(
        accessories.reduce((reducedAccessories, accessory) => ({ ...reducedAccessories, [accessory.sku]: { data: accessory.data } }), {})
    );

    const [additionalSafetyInfo, setAdditionalSafetyInfo] = React.useState<any>({ purchaseDate: null, files: '' });

    const device = useSelector(state => deviceSelector(state));
    const parent: any = useSelector(state => selectedParentSelector(state));
    const postalCode: string = useSelector(state => zipCodeSelector(state));
    const userAgent: string = useSelector(state => userAgentTierSelector(state));
    const soldToId: string = useSelector(state => userSoldToIdSelector(state));
    const { countryCode } = useSelector(state => selectedCountryInfoSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));

    const fullDeviceEligibility = useSelector(state => fullDeviceEligibilitySelector(state));
    const partDeviceEligibility = useSelector(state => partDeviceEligibilitySelector(state));

    React.useEffect(() => {
        if (sectionContainer.current) {
            window.scrollTo(0, sectionContainer.current.offsetTop);
        }
    }, [sectionContainer]);

    function onChangeSelectionClick() {
        if (secondSRCreation) {
            history.push(`/device/orderDetail`);
        }
        else {
            logPageAction('Change selection button for repair items clicked');
            history.push('/device');
        }
    }

    function onSelectPurchaseDate(value: any) {
        setAdditionalSafetyInfo({ ...additionalSafetyInfo, purchaseDate: value });
    }

    const toBase64 = (file: File) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

    function onSelectAttachment(value: any) {

        value && value.forEach(async (file: any) => {
            file.CreationDate = new Date();
            file.MediaTypeName = file.type;
            file.FileName = file.name;
            file.FileSizeBytes = file.size;
            file.ContentData = await (toBase64(file));
        });
        setAdditionalSafetyInfo({ ...additionalSafetyInfo, files: value });
    }

    function onParentProblemTypeChange(value: IComboBoxOption | undefined) {
        setSelectedProblemType(value);
        setSelectedParts([]);
        setSelectedOnDemandFruParts([]);
    }

    function onReplaceComponentChange(option: IChoiceGroupOption | undefined) {
        setPartOrFull(option);
    }

    function onSoftwareSupportEnabled(option: IChoiceGroupOption | undefined) {
        setSoftwareSupport(option);
    }

    function isSdtToolRan(option: IDropdownOption | undefined) {
        setSdtToolRan(option);
    }

    function typeOfResolution(option: IDropdownOption | undefined) {
        setTypeOfResolution(option);
    }

    function onPartSelectionChange(options: IComboBoxOption | undefined) {
        if (!options) {
            return;
        }

        logPageAction('Select Repair Items combo box item selected', { itemsSelected: options.selected });

        if (options.selected) {
            const { selected, ...removeSelectedFromOption } = options;
            setSelectedParts([...selectedParts, removeSelectedFromOption]);
            setSelectedOnDemandFruParts(
                selectedOnDemandFruParts.filter(
                    (part: IComboBoxOption) => part.key !== options.key
                )
            );
        } else {
            setSelectedParts(
                selectedParts.filter(
                    (part: IComboBoxOption) => part.key !== options.key
                )
            );
        }
    }

    function onDemandFruPartSelectionChange(options: IComboBoxOption | undefined) {
        if (!options) {
            return;
        }

        logPageAction('On-Demand Select Repair Items combo box item selected', { itemsSelected: options.selected });

        if (options.selected) {
            const { selected, ...removeSelectedFromOption } = options;
            setSelectedOnDemandFruParts([...selectedOnDemandFruParts, removeSelectedFromOption]);
        } else {
            setSelectedOnDemandFruParts(
                selectedOnDemandFruParts.filter(
                    (part: IComboBoxOption) => part.key !== options.key
                )
            );
        }
    }

    function onCruProofOfPurchaseCheckboxChange(value: boolean | false) {
        setCruProofOfPurchaseSelected(value);
    }

    function onCruPartDeviceEligibleChange(value: boolean | false) {
        setIsCruPartDeviceEligible(value);
    }

    function onDeviceProblemDescriptionChange(value: string | undefined) {
        setDeviceProblemDescription(value);
    }

    function onAccessoryProblemDescriptionChange(targetSKU: any, value: string | undefined) {
        setAccessoriesDescription({
            ...accessoriesDescription,
            [targetSKU.sku]: {
                data: targetSKU.data,
                problemDescription: value,
            },
        });
    }

    function onParentNoOfRepairAttemptsChange(value: IComboBoxOption | undefined) {
        setNoOfRepairAttempts(value);
    }

    function onRepairDetailsDescriptionChange(value: string | undefined) {
        setRepairDetailsDescription(value);
    }

    function onSubmit() {
        if (!areFieldsValid() && isSafetySymptom && userTier.showSafetySection) {
            return;
        }
        if (selectedProblemType && userTier.showSafetySection && selectedProblemType.data?.isSafetySymptom) {
            dispatch(getAddressValidationData(addressValues));
            setShowModal(true);
        } else {
            onSubmitNext();
        }

    }

    function onSubmitNext() {
        logPageAction('Continue to service options: continue button clicked');

        const isFullDeviceRepair: boolean = Boolean(partOrFull && partOrFull.key === 'full');
        const isSoftwareOrder = softwareSupport && softwareSupport?.key && softwareSupport?.key.length > 0;

        let deviceItems = (isFullDeviceRepair || isSoftwareOrder) ? {
            [parent.sku]: {
                ...parent,
                problemDescription: deviceProblemDescription,
                problemType: (selectedProblemType && selectedProblemType.data),
                numberOfRepairAttempts: (noOfRepairAttempts && noOfRepairAttempts?.data),
                repairDetails: repairDetailsDescription,
            },
        } : selectedParts.reduce((options: any, part: any) => {
            return {
                ...options,
                [part.data.symptomCode]: {
                    ...part.data,
                    onDemandFru: false,
                    problemDescription: deviceProblemDescription,
                    problemType: (selectedProblemType && selectedProblemType.data),
                    numberOfRepairAttempts: (noOfRepairAttempts && noOfRepairAttempts?.data),
                    repairDetails: repairDetailsDescription,
                    isCruOffer: cruProofOfPurchaseSelected,
                },
            };
        }, {});

        if (selectedOnDemandFruParts.length > 0) {
            const deviceFruItems = selectedOnDemandFruParts.reduce((options: any, part: any) => {
                return {
                    ...options,
                    [part.data.symptomCode]: {
                        ...part.data,
                        onDemandFru: true,
                        problemDescription: deviceProblemDescription,
                        problemType: (selectedProblemType && selectedProblemType.data),
                        numberOfRepairAttempts: (noOfRepairAttempts && noOfRepairAttempts?.data),
                        repairDetails: repairDetailsDescription,
                        isCruOffer: false,
                    },
                };
            }, {});
            const temp = { ...deviceItems, ...deviceFruItems };
            deviceItems = temp;
        }

        const repairDetails: {} = {
            isFullDeviceRepair,
            isSoftwareOrder,
            deviceItems,
            accessories: partOrFull?.key !== "part" ? accessoriesDescription : {},
        };

        if (isSoftwareOrder) {
            dispatch(getRepairOffer({
                repairDetails,
                additionalInfo: {
                    userAgent,
                    postalCode,
                    countryCode,
                    soldToId,
                    isSoftwareOrder,
                    serialNumber: device.serialNumber,
                },
            }));
            dispatch(saveSoftwareOrderDetails(
                {
                    sdtToolRan: sdtToolRan?.key === 'yes' ? true : false,
                    resolutionType: resolutionType?.text,
                }
            ));
        }
        else {
            dispatch(getRepairOffer({
                repairDetails,
                additionalInfo: {
                    userAgent,
                    postalCode,
                    countryCode,
                    soldToId,
                    serialNumber: device.serialNumber,
                },
            }));
        }

        //if this has no SafetySymptom , progress to the next right here
        if (!(isSafetySymptom && userTier.showSafetySection)) {
            dispatch(
                updateCreateOrderProgress(Progress.RepairOfferSelection)
            );
        }
        else {
            if (additionalSafetyInfo?.files && Array.isArray(additionalSafetyInfo.files)
                && additionalSafetyInfo.files.length > 0) {

                const { files } = additionalSafetyInfo;
                ///Upload the files to azure here
                const attachmentToUpload = {
                    fileNamePrefix: device.serialNumber,
                    files: files.map((file: any) => ({
                        fileName: file.FileName,
                        mediaTypeName: file.MediaTypeName,
                        content: file.ContentData,
                    }
                    )),
                };

                dispatch(uploadSafetyAttachments(attachmentToUpload));
            }
            else {
                storeSafetyIssueInfo();
                dispatch(
                    updateCreateOrderProgress(Progress.RepairOfferSelection)
                );
            }
        }

    }
    const storeSafetyIssueInfo = () => {
        const dataFieldsObj = {
            'FirstName': addressValues.firstName,
            'LastName': addressValues.lastName,
            'Email': addressValues.email,
            'AddressLine1': addressValues.address1,
            'AddressLine2': addressValues.address2,
            'City': addressValues.city,
            'PostalCode': addressValues.postalCode,
            'Country': addressValues.country,
            'PhoneNumber': addressValues.phoneNumber,
            'StateOrProvince': addressValues.stateOrProvince,
        };

        if (isSafetySymptom) {

            const attachments = uploadedAttachmentFilesInfo?.attachmentInfo?.
                map((info: any) => ({
                    FileName: info.fileName,
                    FileContentStorageId: info.fileContentStorageId,
                    MediaTypeName: info.mediaTypeName,
                    FileSizeBytes: info.fileSizeBytes,
                    CreationDate: new Date(),
                }));

            dispatch(
                saveSafetyFormData({
                    DeviceDateOfPurchase: additionalSafetyInfo.purchaseDate,
                    Attachments: attachments,
                    PartnerLocationInfo: dataFieldsObj,
                    PartnerLanguage: addressValues.language,
                    SoldToName: userInfo.soldtoCustomerName,
                })
            );
        }
    };

    const [addressValues, setAddressValues] = React.useState({ ...INITIAL_STATE, email: userInfo.email });

    function setCountryOnChange(value: any) {
        const newAddress = {
            ...addressValues,
            ...value,
        };

        setAddressValues(newAddress);
    }

    function areFieldsValid() {
        if (!addressValues) return false;

        const validation: any = {};
        if (!(deviceProblemDescription && deviceProblemDescription.trim().length)) {
            validation.deviceProblemDescription = t('required_message');
        }
        if (!(addressValues.email && validateEmail(addressValues.email))) {
            validation.email = t('invalid_email_address');
        }
        if (!addressValues.firstName) {
            validation.firstName = t('required_message', { fieldName: t('first_name') });
        }
        if (!addressValues.lastName) {
            validation.lastName = t('required_message', { fieldName: t('last_name') });
        }
        if (!(addressValues.phoneNumber && validatePhoneNumber(addressValues.phoneNumber))) {
            validation.phoneNumber = t('invalid_phone_number');
        }
        if (!addressValues.country) {
            validation.country = t('required_message', { fieldName: t('country') });
        }
        if (provinceValidation[addressValues.country.toLowerCase()]?.required) {
            if (!addressValues.stateOrProvince) {
                validation.stateOrProvince = t('required_message', { fieldName: t('state_province') });
            }
        }
        if (!addressValues.address1) {
            validation.address1 = t('required_message', { fieldName: t('address_1') });
        }
        if (!addressValues.city) {
            validation.city = t('required_message', { fieldName: t('city') });
        }
        if (!(addressValues.postalCode && validatePostalCode(addressValues.country, addressValues.postalCode))) {
            validation.postalCode = t('invalid_postal_code');
        }
        if (Object.keys(validation).length) {
            setAddressValues({ ...addressValues, validation });
            return false;
        }
        return true;
    }



    function isSubmittableForm() {
        const isFullDeviceEligible = fullDeviceEligibility.toLowerCase() === 'eligible';
        const isPartDeviceEligible = partDeviceEligibility.toLowerCase() === 'eligible';

        const devicePart = useSelector(state => devicePartSelector(state));
        const devicePartsOptions: IComboBoxOption[] = selectedProblemType !== undefined && selectedProblemType !== null ? generatePartOptions(devicePart, selectedProblemType, t) : [];
        const isAnyPartAvailable: boolean = devicePartsOptions.length > 0 && !enableCountriesConfig.onCruEnableTiers.includes(userAgentTier);

        if (!parent && Array.isArray(accessories) && accessories.length) {
            return true;
        }

        if (!selectedProblemType || (selectedProblemType.text.toLowerCase() === DESCRIPTION_REQUIRED_PROBLEM_TYPE
            && (!deviceProblemDescription || deviceProblemDescription.length < 50))
        ) {
            return false;
        }

        if (isAnyPartAvailable === true && partOrFull && partOrFull?.key?.toLowerCase() === 'full') {
            let status = false;
            if (noOfRepairAttempts === undefined || noOfRepairAttempts === null) {
                status = false;
            }
            else if (repairDetailsDescription === undefined || repairDetailsDescription === null || repairDetailsDescription.trim() === '') {
                status = false;
            }
            else {
                status = true;
            }
            return status;
        }

        if (isFullDeviceEligible && partOrFull && partOrFull.key.toLowerCase() === 'full') {
            return true;
        }

        if (isPartDeviceEligible && partOrFull && partOrFull.key.toLowerCase() === 'part' && (selectedParts.length || selectedOnDemandFruParts.length)) {
            if (selectedParts.length && isCruPartDeviceEligible) {
                if (cruProofOfPurchaseSelected) {
                    return true;
                }
                return false;
            }
            return true;
        }
        if (softwareSupport) {
            if (sdtToolRan && resolutionType) {
                return true;
            }
            return false;
        }
        return false;
    }

    const isContinueDisabled: boolean = !isSubmittableForm();

    const btnClassName = clsx(Styles.continueButton, {
        [Styles.disabled]: isContinueDisabled,
    });

    function generatePartOptions(devicePart: any[], selectedProblemType: any, t: i18next.TFunction): IComboBoxOption[] {
        const devicePartsOptions: IComboBoxOption[] = devicePart
            .filter((part: any) =>
                selectedProblemType.data.components
                    .toLocaleString()
                    .toLowerCase()
                    .includes(part.symptomCode.toLowerCase())
            )
            .map((part: any) => ({
                key: part.offerToken,
                text: part.name,
                disabled: part.regex && !part.serialNumber,
                data: part,
            }))
            .sort((part: any, part2: any) =>
                part.text.localeCompare(part2.text)
            );

        if (!devicePartsOptions.find((option: any) => option.disabled)) {
            return devicePartsOptions;
        }

        const availableOptions = devicePartsOptions.filter((option: any) => !option.disabled);
        const unAvailableOptions = devicePartsOptions.filter((option: any) => option.disabled);

        return [
            { key: 'Header1', text: t('available_components'), itemType: SelectableOptionMenuItemType.Header },
            ...availableOptions,
            { key: 'divider', text: '-', itemType: SelectableOptionMenuItemType.Divider },
            { key: 'Header2', text: t('unavailable_components'), itemType: SelectableOptionMenuItemType.Header, data: { requiredWarning: true } },
            ...unAvailableOptions,
        ];
    }

    const isSumbitEnabled = userTier.showSafetySection ? !!(
        addressValues.email &&
        addressValues.country &&
        addressValues.country &&
        addressValues.address1 &&
        addressValues.postalCode &&
        addressValues.city &&
        addressValues.stateOrProvince &&
        addressValues.phoneNumber
    ) && attachmentsUploadStatus.status !== LoadingState.started && !!(deviceProblemDescription && deviceProblemDescription.trim().length !== 0) : true;
    return (
        <>
            {showModal && (
                <AddressVerification
                    onChoiceChange={onAddressChoiceChange}
                    options={getAddressChoices()}
                    onCancelChoiceClicked={onCancelAddressChoicePopupClicked}
                    onSaveChoiceClicked={onSaveAddressChoicePopupClicked}
                    onDismissed={onDismissed}
                />
            )}
            <div id="select_repair_items" className={Styles.repairItemContainer} ref={sectionContainer}>
                <header className={Styles.headerSection}>
                    <h2 className={Styles.h2Heading}>
                        {t('select_repair_items')}
                    </h2>
                    <DefaultButton
                        name="changeSelection"
                        className={Styles.changeSelection}
                        text={t('change_selection')}
                        onClick={onChangeSelectionClick}
                    />
                </header>
                <ProblemSelection
                    parent={parent}
                    userTier={userTier}
                    addressValues={addressValues}
                    setCountryOnChange={setCountryOnChange}
                    checkSafetySymptom={checkSafetySymptom}
                    onParentProblemTypeChange={onParentProblemTypeChange}
                    selectedProblemType={selectedProblemType}
                    onReplaceComponentChange={onReplaceComponentChange}
                    onSoftwareSupportEnabled={onSoftwareSupportEnabled}
                    isSdtToolRan={isSdtToolRan}
                    typeOfResolution={typeOfResolution}
                    onPartSelectionChange={onPartSelectionChange}
                    onDemandFruPartSelectionChange={onDemandFruPartSelectionChange}
                    isCruPartDeviceEligible={isCruPartDeviceEligible}
                    onCruPartDeviceEligibleChange={onCruPartDeviceEligibleChange}
                    cruProofOfPurchaseSelected={cruProofOfPurchaseSelected}
                    onCruProofOfPurchaseCheckboxChange={onCruProofOfPurchaseCheckboxChange}
                    partOrFull={partOrFull}
                    selectedParts={selectedParts}
                    selectedOnDemandFruParts={selectedOnDemandFruParts}
                    deviceProblemDescription={deviceProblemDescription}
                    onParentProblemDescriptionChange={
                        onDeviceProblemDescriptionChange
                    }
                    onSelectPurchaseDate={onSelectPurchaseDate}
                    onSelectAttachment={onSelectAttachment}
                    noOfRepairAttempts={noOfRepairAttempts}
                    onNoOfRepairAttemptsChange={
                        onParentNoOfRepairAttemptsChange
                    }
                    repairDetailsDescription={repairDetailsDescription}
                    onRepairDetailsDescriptionChange={
                        onRepairDetailsDescriptionChange
                    }
                />
                {partOrFull?.key !== "part" && <AccessorySection
                    accessories={accessories}
                    onAccessoryProblemDescriptionChange={
                        onAccessoryProblemDescriptionChange
                    }
                />}
            </div>
            <ErrorMessage
                message={attachmentsUploadStatus.status === LoadingState.rejected ?
                    t('attachment_upload_error_message') : ''}

            />
            <section className={Styles.continueButtonContainer}>
                {isContinueDisabled}
                <DefaultButton
                    name="continue"
                    className={btnClassName}
                    text={t('continue')}
                    disabled={isSafetySymptom ? !isSumbitEnabled : isContinueDisabled}
                    onClick={onSubmit}
                />
                {attachmentsUploadStatus.status === LoadingState.started &&
                    <Spinner
                        className={Styles.spinner}
                        size={SpinnerSize.small}
                    />}
            </section>
        </>
    );
}